import { useEffect } from 'react'

import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useFormField,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { TimeWindowException } from './components/TimeWindowException'

export interface TimeWindowExceptionProps {
  testId?: string
  label: string
  disabled: boolean
  h24?: boolean
}

export type TimeWindowExceptionFieldValue = Record<string, uui.domain.rm.TimeWindow[]>
const defaultComponentValue: TimeWindowExceptionFieldValue = {}

export type TimeWindowExceptionFieldProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = TimeWindowExceptionFieldValue,
> = {
  timeWindowProps: TimeWindowExceptionProps
} & TidalFormBridgeOptions<TimeWindowExceptionFieldValue, TargetField, State, AcceptedFormValue>

export function TimeWindowExceptionField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = TimeWindowExceptionFieldValue,
>(props: TimeWindowExceptionFieldProps<TargetField, State, AcceptedFormValue>) {
  const {
    timeWindowProps: { testId, label, disabled },
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: { value, onChange, fieldDisabled, errorOrHelperText },
  } = useTidalFormBridge<TimeWindowExceptionFieldValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const {
    fieldApi: { validate },
  } = useFormField<TargetField, State>(props.name)

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (Object.keys(value).length > 2) {
        console.error('TimeWindowsExceptionsField: value has more than 2 elements')
      }
    }
  }, [value])

  return (
    <TimeWindowException
      testId={testId}
      label={label}
      value={value}
      disabled={fieldDisabled || disabled}
      onChange={onChange}
      validate={validate}
      helperText={errorOrHelperText}
    />
  )
}
